/**
 * SkillsPage -- Manage skills in the content library.
 *
 * Skills grouped by category with add/edit/delete.
 * Shows proficiency level and years experience.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useContentStore, useContentActions } from '../../state/cms/useContentStore';
import { Panel } from '../../components/ui/Panel';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/ui/LoadingState';
import { AiAssistButton } from '../../components/cms/AiAssistButton';
import { AiSuggestionPanel } from '../../components/cms/AiSuggestionPanel';
import { useSaveHandler } from '../../hooks/useSaveHandler';
import { useAiAssist } from '../../hooks/useAiAssist';
import { aiApi } from '../../services/ai.api';
import { logger } from '../../services/logger.client';
import type { Skill, SkillCategory, ProficiencyLevel } from '../../types/cms.types';
import type { GenerateContentData } from '../../types/ai.types';

const log = logger.child({ module: 'SkillsPage' });

const CATEGORIES: SkillCategory[] = [
  'Languages',
  'Frameworks',
  'Tools',
  'Cloud',
  'Databases',
  'Practices',
  'Other',
];

const PROFICIENCY_LEVELS: ProficiencyLevel[] = [
  'beginner',
  'intermediate',
  'advanced',
  'expert',
];

export function SkillsPage() {
  const skills = useContentStore((s) => s.skills);
  const isLoading = useContentStore((s) => s.isLoading);
  const error = useContentStore((s) => s.error);
  const { fetchSkills, createSkill, updateSkill, deleteSkill } =
    useContentActions();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<SkillCategory>('Languages');
  const [proficiency, setProficiency] = useState<ProficiencyLevel>('intermediate');
  const [years, setYears] = useState('');
  const { isSaving, saveMessage, saveError, execute: executeSave } = useSaveHandler({
    getStoreError: () => useContentStore.getState().error,
  });

  /* ---- AI Assist: Skills narrative generation ---- */
  const [narrative, setNarrative] = useState('');
  const narrativeAi = useAiAssist<
    { type: 'skills_narrative'; context: { skills: string[]; domain?: string } },
    GenerateContentData
  >({
    apiCall: (input) => aiApi.generateContent(input),
    onAccept: (data) => {
      setNarrative(data.content);
    },
  });

  useEffect(() => {
    log.info('SkillsPage mounted');
    void fetchSkills();
  }, [fetchSkills]);

  function resetForm() {
    setName('');
    setCategory('Languages');
    setProficiency('intermediate');
    setYears('');
    setEditingId(null);
    setShowForm(false);
  }

  function handleEdit(skill: Skill) {
    setName(skill.name);
    setCategory(skill.category);
    setProficiency(skill.proficiency_level);
    setYears(skill.years_experience?.toString() ?? '');
    setEditingId(skill.id);
    setShowForm(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    const data: Partial<Skill> = {
      name: name.trim(),
      category,
      proficiency_level: proficiency,
      years_experience: years ? parseFloat(years) : null,
      display_order: editingId
        ? skills.find((s) => s.id === editingId)?.display_order ?? 0
        : skills.length,
    };

    await executeSave(
      () => (editingId ? updateSkill(editingId, data) : createSkill(data)),
      { onSuccess: resetForm },
    );
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this skill?')) return;
    await deleteSkill(id);
  }

  /* Group skills by category */
  const grouped = CATEGORIES.map((cat) => ({
    category: cat,
    skills: skills.filter((s) => s.category === cat),
  })).filter((g) => g.skills.length > 0);

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Skills</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Add Skill
        </Button>
      </header>

      {saveError && (
        <span className="editor-page__save-msg editor-page__save-msg--error">
          {saveError}
        </span>
      )}
      {saveMessage && (
        <span className="editor-page__save-msg">{saveMessage}</span>
      )}

      {showForm && (
        <Panel>
          <Panel.Header>{editingId ? 'Edit Skill' : 'New Skill'}</Panel.Header>
          <Panel.Content>
            <form className="editor-field-group" onSubmit={handleSubmit} noValidate>
              <Input
                label="Skill Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. TypeScript"
                required
              />
              <div className="experience-entry__row">
                <div className="input-wrapper">
                  <label className="input-label">Category</label>
                  <select
                    className="input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as SkillCategory)}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-wrapper">
                  <label className="input-label">Proficiency</label>
                  <select
                    className="input"
                    value={proficiency}
                    onChange={(e) =>
                      setProficiency(e.target.value as ProficiencyLevel)
                    }
                  >
                    {PROFICIENCY_LEVELS.map((p) => (
                      <option key={p} value={p}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Input
                label="Years Experience"
                type="number"
                value={years}
                onChange={(e) => setYears(e.target.value)}
                placeholder="e.g. 5"
              />
              <div className="cms-page__actions-row">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={resetForm}
                  type="button"
                >
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" loading={isSaving}>
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </Panel.Content>
        </Panel>
      )}

      {/* AI Skills Narrative -- generates a cohesive paragraph from the skill list */}
      {skills.length >= 2 && (
        <Panel>
          <Panel.Header>
            <div className="ai-label-row">
              Skills Narrative
              <AiAssistButton
                onClick={() =>
                  void narrativeAi.trigger({
                    type: 'skills_narrative',
                    context: {
                      skills: skills.map((s) => s.name),
                    },
                  })
                }
                isLoading={narrativeAi.isLoading}
                disabled={skills.length < 2}
                label="Generate skills narrative with AI"
              />
            </div>
          </Panel.Header>
          <Panel.Content>
            {narrative && !narrativeAi.suggestion && !narrativeAi.isLoading && (
              <p className="ai-suggestion-panel__text">{narrative}</p>
            )}
            {!narrative && !narrativeAi.suggestion && !narrativeAi.isLoading && !narrativeAi.error && (
              <p className="list-empty">
                Click the sparkle button to generate a narrative paragraph from your skills.
              </p>
            )}
            <AiSuggestionPanel
              suggestion={narrativeAi.suggestion ? narrativeAi.suggestion.content : null}
              error={narrativeAi.error}
              isLoading={narrativeAi.isLoading}
              onAccept={narrativeAi.accept}
              onDismiss={narrativeAi.dismiss}
            />
          </Panel.Content>
        </Panel>
      )}

      <LoadingState loading={isLoading} error={error}>
        {grouped.length === 0 ? (
          <div className="list-empty">
            No skills yet. Click '+ Add Skill' to get started.
          </div>
        ) : (
          <div className="cms-page__body">
            {grouped.map((group) => (
              <Panel key={group.category}>
                <Panel.Header>
                  {group.category} ({group.skills.length})
                </Panel.Header>
                <Panel.Content>
                  <div className="cms-skill-list">
                    {group.skills.map((skill) => (
                      <div key={skill.id} className="cms-skill-item">
                        <div className="cms-skill-item__info">
                          <span className="cms-skill-item__name">{skill.name}</span>
                          <span className="badge badge--active">
                            {skill.proficiency_level}
                          </span>
                          {skill.years_experience !== null && (
                            <span className="cms-skill-item__years">
                              {skill.years_experience} yr{skill.years_experience !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div className="cms-skill-item__actions">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEdit(skill)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => void handleDelete(skill.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel.Content>
              </Panel>
            ))}
          </div>
        )}
      </LoadingState>
    </div>
  );
}
